import type { FeatureFlags } from "../schema/clientConfig.js";

export type BillingInterval = "monthly" | "yearly";

export type AddOnId =
  | "online-ordering"
  | "service-booking"
  | "table-reservations"
  | "sms-reminders"
  | "review-engine"
  | "cms";

export interface BasePlanDefinition {
  monthlyPrice: number;
  yearlyPrice: number; // 10 months — 2 months free
  tagline: string;
  features: string[];
}

export interface AddOnDefinition {
  id: AddOnId;
  label: string;
  description: string;
  monthlyPrice: number;
  yearlyPrice: number; // 10 months — 2 months free
  flag: keyof FeatureFlags;
  category: "revenue" | "automation";
  comingSoon?: boolean;
}

export const BASE_PLAN: BasePlanDefinition = {
  monthlyPrice: 129,
  yearlyPrice: 1290,
  tagline: "A custom website, hosting, and weekly updates — done for you.",
  features: [
    "Your own website — built and maintained for you",
    "Looks great on every phone, tablet, and desktop",
    "Fast, secure hosting with your custom domain",
    "Contact form that captures leads directly to you",
    "Monthly report: traffic, clicks, and top pages",
    "Shows up in Google search results",
  ],
};

export const ADD_ONS: AddOnDefinition[] = [
  {
    id: "online-ordering",
    label: "Order Online + Delivery",
    description: "Allow customers to online order and deliver with Doordash.",
    monthlyPrice: 75,
    yearlyPrice: 750,
    flag: "online_ordering",
    category: "revenue",
    comingSoon: true,
  },
  {
    id: "service-booking",
    label: "Service & Appointment Booking",
    description: "Time-slot booking with deposits for service businesses.",
    monthlyPrice: 50,
    yearlyPrice: 500,
    flag: "appointments",
    category: "revenue",
  },
  {
    id: "table-reservations",
    label: "Table Reservations",
    description: "Lightweight alternative to OpenTable — diner data stays yours.",
    monthlyPrice: 40,
    yearlyPrice: 400,
    flag: "reservations",
    category: "revenue",
  },
  {
    id: "sms-reminders",
    label: "SMS Integration",
    description: "Text yourself or customers for all business critical activities.",
    monthlyPrice: 45,
    yearlyPrice: 450,
    flag: "sms_reminders",
    category: "automation",
  },
  {
    id: "review-engine",
    label: "SEO + Google Review",
    description: "Optimize, manage, and automate SEO and Google Review processes.",
    monthlyPrice: 60,
    yearlyPrice: 600,
    flag: "review_engine",
    category: "automation",
  },
  {
    id: "cms",
    label: "CMS Module",
    description: "Edit your own menu, hours, photos, and content anytime.",
    monthlyPrice: 60,
    yearlyPrice: 600,
    flag: "cms",
    category: "automation",
  },
];

export const ADD_ON_MAP = new Map<AddOnId, AddOnDefinition>(
  ADD_ONS.map((a) => [a.id, a]),
);
